import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Bell, X, CalendarBlank, Clock, CloudRain, Sun, CreditCard, PencilLine, CaretRight } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  scheduled_post: CalendarBlank,
  best_time: Clock,
  weekly_recap: PencilLine,
  seasonal_tip: Sun,
  subscription: CreditCard,
};

const PRIORITY_STYLES = {
  high: 'border-l-[#EF4444]',
  medium: 'border-l-[#8B5CF6]',
  low: 'border-l-[#3F3F46]',
};

export default function NotificationCenter({ onNavigate }) {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(new Set());
  const [permissionAsked, setPermissionAsked] = useState(false);
  const panelRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(data.notifications || []);
      // Browser notification for high priority
      if ('Notification' in window && Notification.permission === 'granted') {
        data.notifications.filter(n => n.priority === 'high' && !dismissed.has(n.id)).forEach(n => {
          new Notification('Shanga Pro', { body: n.message, icon: '/logo192.png', tag: n.id });
        });
      }
    } catch (error) { console.error("Notification fetch error:", error.message); }
  }, [dismissed]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000); // Every 5 min
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Ask for browser notification permission
  useEffect(() => {
    if (!permissionAsked && 'Notification' in window && Notification.permission === 'default') {
      setPermissionAsked(true);
    }
  }, [permissionAsked]);

  const requestPermission = async () => {
    if ('Notification' in window) {
      await Notification.requestPermission();
      setPermissionAsked(false);
    }
  };

  // Close panel on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const dismiss = (id) => setDismissed(prev => new Set([...prev, id]));
  const activeNotifications = notifications.filter(n => !dismissed.has(n.id));
  const highCount = activeNotifications.filter(n => n.priority === 'high').length;

  const handleAction = (notif) => {
    if (notif.action === 'generate' && onNavigate) onNavigate('generator');
    if (notif.action === 'subscribe' && onNavigate) onNavigate('subscription');
    setOpen(false);
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notification-center">
      {/* Bell button */}
      <button onClick={() => setOpen(!open)} data-testid="notification-bell"
        className="relative h-9 w-9 flex items-center justify-center rounded-lg hover:bg-[#18181B] transition-colors text-[#A1A1AA] hover:text-[#F4F4F5]">
        <Bell size={20} weight={activeNotifications.length > 0 ? "fill" : "duotone"} />
        {activeNotifications.length > 0 && (
          <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center px-1 ${
            highCount > 0 ? 'bg-[#EF4444] text-white' : 'bg-[#8B5CF6] text-white'
          }`} data-testid="notification-badge">
            {activeNotifications.length}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div className="absolute right-0 top-12 w-80 sm:w-96 bg-[#18181B] border border-[#27272A] rounded-lg shadow-2xl shadow-black/50 z-50 animate-fade-in-up overflow-hidden" data-testid="notification-panel">
          <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between">
            <h3 className="font-bold text-sm text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Notifications</h3>
            <span className="text-xs text-[#71717A]">{activeNotifications.length} active{activeNotifications.length > 1 ? 's' : ''}</span>
          </div>

          {/* Permission request */}
          {permissionAsked && 'Notification' in window && Notification.permission === 'default' && (
            <div className="px-4 py-3 bg-[#8B5CF6]/10 border-b border-[#27272A]">
              <p className="text-xs text-[#A78BFA] mb-2">Activez les notifications pour ne rien manquer</p>
              <button onClick={requestPermission} data-testid="enable-notifications-btn"
                className="h-8 px-3 text-xs bg-[#8B5CF6] text-white rounded-lg font-medium hover:bg-[#7C3AED]">
                Activer
              </button>
            </div>
          )}

          <div className="max-h-80 overflow-y-auto">
            {activeNotifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell size={24} weight="duotone" className="mx-auto text-[#3F3F46] mb-2" />
                <p className="text-sm text-[#71717A]">Aucune notification</p>
              </div>
            ) : (
              activeNotifications.map(n => {
                const Icon = ICON_MAP[n.type] || Bell;
                const priorityStyle = PRIORITY_STYLES[n.priority] || PRIORITY_STYLES.low;
                return (
                  <div key={n.id} className={`px-4 py-3 border-b border-[#27272A] border-l-2 ${priorityStyle} hover:bg-[#1F1F23] transition-colors`} data-testid={`notification-${n.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2.5 flex-1">
                        <Icon size={16} weight="duotone" className="text-[#A78BFA] mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-[#F4F4F5] mb-0.5">{n.title}</p>
                          <p className="text-xs text-[#A1A1AA] leading-relaxed">{n.message}</p>
                          {n.action && (
                            <button onClick={() => handleAction(n)} className="mt-1.5 text-[10px] font-bold text-[#8B5CF6] hover:text-[#A78BFA] flex items-center gap-0.5">
                              {n.action === 'generate' ? 'Créer un post' : 'Renouveler'} <CaretRight size={10} weight="bold" />
                            </button>
                          )}
                        </div>
                      </div>
                      <button onClick={() => dismiss(n.id)} className="text-[#3F3F46] hover:text-[#71717A] shrink-0 p-0.5">
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
